/**
 * The check runner (W2-12a checker-runtime, incremental passes W3-08; contract core/check/types.ts
 * `CreateCheckRunner`; DESIGN.md §5.10, §7.6; ARCHITECTURE.md §9).
 *
 * A runner owns the rule set, the enabled rule ids, the thresholds and the check environment (Phira player
 * settings, skin metrics, reference viewport, song length) and the RESULT of the passes so far. A pass runs the
 * enabled rules over the document the context exposes: 'chart' rules once, 'line' rules once per line in export
 * order. The drafts the rules emit become Problems (rule id, category, severity = the draft's override or the rule's
 * default, subject to the severity policy below) and the result is diffed against the previous one by problem id:
 * only the differences leave the analysis worker.
 *
 * Incremental passes (UX-092): what a rule reported is kept per WORK UNIT (a chart rule, or one line of a line rule),
 * and a pass is given the scope of what changed since the last one (`PassScope`, accumulated from the transactions'
 * DirtyScopes by the analysis session). A pass re-runs only the units the change can affect and keeps the others:
 *  - everything: a full scope (init, resync, configuration, "Check now"), a change of the rules, thresholds,
 *    environment or level (the runner marks its next pass full by itself), a tempo, timing or package change (they
 *    move every note and every message time), and a change of the line set (lines added, removed or reordered:
 *    unnamed lines are labelled by their index);
 *  - a 'chart' rule runs again when the change touches one of its `deps` or any line record (rules may skip lines by
 *    their type or name them in messages);
 *  - a 'line' rule runs again for every line when the change touches one of its global `deps` (meta, assets,
 *    effects), else only for the lines whose notes, motion or record changed, plus the children (recursively) of the
 *    lines whose motion or record changed: a parent carries its children along;
 *  - a rule that did not run completely before (newly enabled, shown again at the Guided level) runs everywhere;
 *    one that is no longer run (disabled, hidden at the Guided level) drops its problems.
 * `full()` and `begin()` without a scope are full passes (the contract's form).
 *
 * Phases and partial results: the 'phira' rules (the only ones that can be fatal and block an export, plus Phira's
 * own compatibility warnings) run first, then the gameplay and style rules. Passes are steppable (`begin()` returns
 * a `CheckPass` that runs work units until told to yield), so the analysis worker can slice a pass, let newer
 * transactions in between and stream what the rules that finished found (`takeDiff()`), and tell the main thread
 * when the first phase is done (`blockingDone`; the export preflight waits only for it). Results of finished rules
 * become the runner's result only when they are taken (`takeDiff()` or `commit()`), so an abandoned pass changes
 * nothing it did not report; its scope is merged into the next pass's. `full(isStale)` is the synchronous form of
 * the contract: it polls `isStale()` between work units and returns null when it says the pass is stale.
 *
 * Severity policy (DESIGN.md §7.6): only the rules whose failure makes Phira refuse or crash on the chart may be
 * fatal (fatal problems block quick export): PH-01 ... PH-07, PH-35 and PH-36. A 'fatal' severity from any other
 * rule is lowered to 'error'.
 *
 * Level (contract: "rules marked 'full' are hidden (not run) in Guided unless the chart uses the feature";
 * DESIGN.md §2.4): in the Guided level a 'full' rule runs only when the chart uses its feature. A rule says what that
 * means with the optional `usesFeature(ctx)` probe (CheckRule contract, CR-W2-12a-5), asked once at the
 * start of every Guided pass: false skips the rule (its problems leave the list until the chart uses the feature or
 * the level is Full), true runs it as in Full. A 'full' rule without a probe runs in both levels: the rules of that
 * kind report only entities of their Full-level feature (imported RPE fields, layers, UI movers, distance effects,
 * Beziers, ...), so scanning for them is the use test itself. A probe that throws is reported like a failing rule
 * and the rule runs (a broken probe never hides problems). The UI side of the table, tips collapsed in Guided, is
 * the Problems panel's (ProblemsPanel.tsx).
 *
 * A rule that throws is isolated: the failure is reported through `onRuleError`, the other rules still run, and
 * the rule's problems of the PREVIOUS passes are kept (a crashing rule never makes its problems disappear silently);
 * the pass lists the rule in `failedRules`, which the analysis session sends with its stats so the Problems panel
 * can say which checks did not run. Drafts without an id or a message are dropped and reported the same way.
 */

import type { AssetId, DocIndexes, LineId, Note, NoteId } from '../model/types';
import type { DirtyScopes, Op } from '../ops/types';
import type { Beat } from '../time/types';
import { inWindow, isWindowedRule, type NoteWindow } from './geometry';
import type {
  CheckCategory,
  CheckContext,
  CheckDep,
  CheckRule,
  CheckRunner,
  CheckThresholds,
  CreateCheckRunner,
  Problem,
  ProblemDiff,
  ProblemDraft,
  Severity,
} from './types';

// ---------------------------------------------------------------------------------------------------------------
// Severity policy
// ---------------------------------------------------------------------------------------------------------------

/** Rules that may report 'fatal' problems (Phira fails to load the chart or crashes; DESIGN.md §7.6). */
export const FATAL_RULE_IDS: ReadonlySet<string> = new Set([
  'PH-01',
  'PH-02',
  'PH-03',
  'PH-04',
  'PH-05',
  'PH-06',
  'PH-07',
  'PH-35',
  'PH-36',
]);

/** The severity a problem of `rule` is reported with: 'fatal' only for the rules of FATAL_RULE_IDS. */
export function applySeverityPolicy(rule: string, severity: Severity): Severity {
  return severity === 'fatal' && !FATAL_RULE_IDS.has(rule) ? 'error' : severity;
}

/** Severity rank, most severe first (sorting, counts, decorations). */
export const SEVERITY_RANK: Readonly<Record<Severity, number>> = Object.freeze({
  fatal: 0,
  error: 1,
  warning: 2,
  tip: 3,
});

export const SEVERITIES: readonly Severity[] = Object.freeze(['fatal', 'error', 'warning', 'tip']);

/** Category of the rules that run first (the only ones that can block an export, plus Phira compatibility). */
export const BLOCKING_CATEGORY: CheckCategory = 'phira';

// ---------------------------------------------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------------------------------------------

/** Player settings, skin, reference viewport and song length (with the asset it was decoded from) a pass uses. */
export type CheckEnvironment = Pick<CheckContext, 'player' | 'skin' | 'refViewport' | 'songLengthSec'> & {
  readonly songLengthAsset: AssetId | null;
};

export type CheckLevel = 'guided' | 'full';

/** A rule (or one line of a line rule) that threw, or emitted an invalid draft. */
export interface RuleFailure {
  readonly rule: string;
  readonly line?: LineId;
  readonly error: unknown;
}

/** One pass in progress. */
export interface CheckPass {
  /** Every work unit ran. */
  readonly done: boolean;
  readonly unitsDone: number;
  readonly unitsTotal: number;
  /** Every rule of the first phase (BLOCKING_CATEGORY) ran: problems that can block an export are known. */
  readonly blockingDone: boolean;
  /** Work units of the first phase in this pass (0: nothing that can block an export is checked again). */
  readonly blockingUnits: number;
  /**
   * What the next pass must cover when this one is abandoned (merged into its scope): the scope of an incremental
   * pass; nothing for a pass that runs everything (the rules it did not finish are incomplete and run everywhere).
   */
  readonly scope: PassScope;
  /** Rules that threw in this pass, sorted (their previous problems are kept). */
  readonly failedRules: readonly string[];
  /**
   * Runs work units until every unit ran or `shouldYield()` returns true (asked after each unit, so at least one
   * unit runs per call). Returns `done`.
   */
  run(shouldYield?: () => boolean): boolean;
  /**
   * Makes what the rules that finished so far found the runner's result and returns the difference (partial
   * results while the pass goes on; empty when nothing finished since the last call).
   */
  takeDiff(): ProblemDiff;
  /** Takes the rest (the pass must be done) and returns the difference not taken yet. */
  commit(): ProblemDiff;
}

export interface SteppedCheckRunner extends CheckRunner {
  /** Starts a pass over the current document, rules and settings; without a scope (or a full one) every unit runs. */
  begin(scope?: PassScope): CheckPass;
  /** Rules sorted by id. */
  readonly rules: readonly CheckRule[];
  readonly enabled: ReadonlySet<string>;
  readonly thresholds: CheckThresholds;
  readonly environment: CheckEnvironment;
  readonly level: CheckLevel;
  setLevel(level: CheckLevel): void;
  /** Forgets the current result (the next committed pass reports every problem as added). */
  reset(): void;
}

export interface CheckRunnerOptions {
  readonly onRuleError?: (f: RuleFailure) => void;
}

/** True when a diff changes nothing. */
export function isEmptyDiff(d: ProblemDiff): boolean {
  return d.added.length === 0 && d.removed.length === 0 && d.updated.length === 0;
}

// ---------------------------------------------------------------------------------------------------------------
// Pass scope
// ---------------------------------------------------------------------------------------------------------------

/**
 * What changed since the last pass: accumulated from the DirtyScopes of the transactions the replica applied
 * (`addDirty`), or everything (`all`). Mutable; the analysis session keeps one and hands it to the next pass.
 */
export class PassScope {
  /** Beat spans (start, end) of every note a change inserted, removed or edited: before and after the edit. */
  readonly noteSpans: Array<readonly [Beat, Beat]> = [];
  /** A note change whose spans are not known (`addDirty` without the ops): no time windows. */
  noteSpansUnknown = false;
  all = false;
  tempo = false;
  timing = false;
  meta = false;
  pkg = false;
  assets = false;
  effects = false;
  lineSet = false;
  readonly lineNotes = new Set<LineId>();
  readonly lineMotion = new Set<LineId>();
  readonly lineProps = new Set<LineId>();

  /** A scope covering everything. */
  static full(): PassScope {
    const s = new PassScope();
    s.all = true;
    return s;
  }

  /**
   * Adds what one applied change touched; with its ops and the notes after it, also where the changed notes are
   * (time windows for the pair rules).
   */
  addChange(d: DirtyScopes, ops: readonly Op[], notes: ReadonlyMap<NoteId, Note>): void {
    this.addDirty(d, true);
    for (const op of ops) {
      if (op.o === 'ins' || op.o === 'del') {
        if (op.tbl === 'notes') this.noteSpans.push([op.rec.t, op.rec.end]);
        continue;
      }
      if (op.o !== 'set' || op.tbl !== 'notes') continue;
      const cur = notes.get(op.id);
      const prev = op.prev as Partial<Note>;
      const next = op.next as Partial<Note>;
      const before: readonly [Beat | undefined, Beat | undefined] = [prev.t ?? cur?.t, prev.end ?? cur?.end];
      const after: readonly [Beat | undefined, Beat | undefined] = [next.t ?? cur?.t, next.end ?? cur?.end];
      for (const [t, end] of [before, after]) {
        if (t === undefined || end === undefined) this.noteSpansUnknown = true;
        else this.noteSpans.push([t, end]);
      }
    }
  }

  /** Adds what one applied change touched (without the ops: the changed notes' times are unknown). */
  addDirty(d: DirtyScopes, spansFollow = false): void {
    if (!spansFollow && d.lineNotes.size > 0) this.noteSpansUnknown = true;
    this.tempo ||= d.tempo;
    this.timing ||= d.timing;
    // The RPE root (RPEVersion, unknown root keys) is package data and metadata as far as the rules go.
    this.meta ||= d.meta || d.rpeRoot;
    this.pkg ||= d.pkg || d.rpeRoot;
    this.assets ||= d.assets;
    this.effects ||= d.effects;
    this.lineSet ||= d.lineSet;
    for (const l of d.lineNotes) this.lineNotes.add(l);
    for (const l of d.lineMotion.keys()) this.lineMotion.add(l);
    for (const l of d.lineProps) this.lineProps.add(l);
  }

  /** Adds another scope (an abandoned pass's). */
  merge(o: PassScope): void {
    this.noteSpans.push(...o.noteSpans);
    this.noteSpansUnknown ||= o.noteSpansUnknown;
    this.all ||= o.all;
    this.tempo ||= o.tempo;
    this.timing ||= o.timing;
    this.meta ||= o.meta;
    this.pkg ||= o.pkg;
    this.assets ||= o.assets;
    this.effects ||= o.effects;
    this.lineSet ||= o.lineSet;
    for (const l of o.lineNotes) this.lineNotes.add(l);
    for (const l of o.lineMotion) this.lineMotion.add(l);
    for (const l of o.lineProps) this.lineProps.add(l);
  }

  /** Nothing changed. */
  get empty(): boolean {
    return (
      !this.all &&
      !this.tempo &&
      !this.timing &&
      !this.meta &&
      !this.pkg &&
      !this.assets &&
      !this.effects &&
      !this.lineSet &&
      this.lineNotes.size === 0 &&
      this.lineMotion.size === 0 &&
      this.lineProps.size === 0
    );
  }

  /** Every unit of every rule has to run (see the module header). */
  get everything(): boolean {
    return this.all || this.tempo || this.timing || this.pkg || this.lineSet;
  }

  /** Whether the change touches a dependency (`lines` also counts for every line record that changed). */
  touches(dep: CheckDep): boolean {
    switch (dep) {
      case 'notes':
        return this.lineNotes.size > 0;
      case 'motion':
        return this.lineMotion.size > 0;
      case 'lines':
        return this.lineSet || this.lineProps.size > 0;
      case 'tempo':
        return this.tempo;
      case 'timing':
        return this.timing;
      case 'meta':
        return this.meta;
      case 'pkg':
        return this.pkg;
      case 'assets':
        return this.assets;
      case 'effects':
        return this.effects;
    }
  }

  /**
   * Lines whose own notes, motion or record changed, plus every descendant of a line whose motion or record changed
   * (children move with their parent); lines no longer in the document are left out.
   */
  dirtyLines(idx: Pick<DocIndexes, 'childrenOf'>, exists: (l: LineId) => boolean): Set<LineId> {
    const out = new Set<LineId>();
    for (const l of this.lineNotes) if (exists(l)) out.add(l);
    const expanded = new Set<LineId>();
    const stack: LineId[] = [...this.lineMotion, ...this.lineProps];
    for (let l = stack.pop(); l !== undefined; l = stack.pop()) {
      if (expanded.has(l) || !exists(l)) continue;
      expanded.add(l);
      out.add(l);
      for (const c of idx.childrenOf(l)) stack.push(c);
    }
    return out;
  }
}

// ---------------------------------------------------------------------------------------------------------------
// Problems from drafts
// ---------------------------------------------------------------------------------------------------------------

/** The Problem a rule's draft becomes (rule defaults, draft severity override, severity policy). */
export function problemFromDraft(rule: Pick<CheckRule, 'id' | 'category' | 'severity'>, d: ProblemDraft): Problem {
  const { severity, ...rest } = d;
  return {
    ...rest,
    rule: rule.id,
    category: rule.category,
    severity: applySeverityPolicy(rule.id, severity ?? rule.severity),
  };
}

function draftError(d: ProblemDraft): string | null {
  if (d.id.length === 0) return 'problem draft without an id';
  if (d.message.key.length === 0) return `problem ${d.id} without a message`;
  return null;
}

/** Structural signature used to detect updated problems (problems are plain JSON data). */
function signature(p: Problem): string {
  return JSON.stringify(p);
}

// ---------------------------------------------------------------------------------------------------------------
// Runner
// ---------------------------------------------------------------------------------------------------------------

/** Dependencies whose change re-runs a line rule on every line (they are not per line). */
const GLOBAL_LINE_DEPS: readonly CheckDep[] = ['meta', 'assets', 'effects'];

/** Slack added around time windows (seconds): tempo conversions and the rules' own epsilons. */
const WINDOW_SLACK_SEC = 1e-3;

/** A work unit's key: the rule id, plus the line for line rules. */
type UnitKey = string;

/** Rule id of a problem id (`${rule}:${entityKey}`). */
function ruleOfId(id: string): string {
  const i = id.indexOf(':');
  return i < 0 ? id : id.slice(0, i);
}

function unitKey(rule: string, line?: LineId): UnitKey {
  return line === undefined ? rule : `${rule}@${String(line)}`;
}

interface Entry {
  readonly problem: Problem;
  sig: string | null;
  /** The unit that reported it (the first one, when two units report the same id). */
  readonly unit: UnitKey;
}

function sigOf(e: Entry): string {
  e.sig ??= signature(e.problem);
  return e.sig;
}

/** One rule's work in a pass. */
interface Job {
  readonly rule: CheckRule;
  /** Lines to run (line rules), in export order; null for a chart rule. */
  readonly lines: readonly LineId[] | null;
  /** Every unit of the rule runs: units of the previous result that do not run now are dropped. */
  readonly complete: boolean;
  /**
   * A windowed chart rule re-checked around changed notes: problems anchored (at.sec) inside `anchors` are replaced,
   * the others kept; the rule sees the notes of `notes` only.
   */
  readonly window?: { readonly anchors: NoteWindow; readonly notes: NoteWindow };
}

/** What a pass hands to `takeDiff`, in order: units as they finish, and the end of every complete job. */
type Finished =
  | {
      readonly kind: 'unit';
      readonly rule: string;
      readonly unit: UnitKey;
      readonly problems: ReadonlyMap<string, Problem>;
      readonly anchors?: NoteWindow;
    }
  | { readonly kind: 'job'; readonly rule: string; readonly units: ReadonlySet<UnitKey> };

const NO_PROBLEMS: ReadonlyMap<string, Problem> = new Map();

/** A diff under construction: an id removed and reported again in the same diff is an update. */
class DiffBuilder {
  private readonly added = new Map<string, Problem>();
  private readonly updated = new Map<string, Problem>();
  private readonly removed = new Set<string>();

  add(p: Problem): void {
    if (this.removed.delete(p.id)) this.updated.set(p.id, p);
    else this.added.set(p.id, p);
  }

  update(p: Problem): void {
    if (this.added.has(p.id)) this.added.set(p.id, p);
    else this.updated.set(p.id, p);
  }

  remove(id: string): void {
    if (this.added.delete(id)) return;
    this.updated.delete(id);
    this.removed.add(id);
  }

  take(): ProblemDiff {
    const d: ProblemDiff = {
      added: [...this.added.values()],
      removed: [...this.removed],
      updated: [...this.updated.values()],
    };
    this.added.clear();
    this.updated.clear();
    this.removed.clear();
    return d;
  }
}

/** Sink that receives the emissions of the unit currently running. */
interface Sink {
  emit(d: ProblemDraft): void;
}

function byId(a: CheckRule, b: CheckRule): number {
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

/** Run order: the blocking phase (BLOCKING_CATEGORY) first, then by id. */
function byPhaseThenId(a: CheckRule, b: CheckRule): number {
  const pa = a.category === BLOCKING_CATEGORY ? 0 : 1;
  const pb = b.category === BLOCKING_CATEGORY ? 0 : 1;
  return pa - pb || byId(a, b);
}

/** Sorted, merged ranges. */
function mergeRanges(ranges: Array<readonly [number, number]>): NoteWindow {
  const sorted = ranges.filter(([a, b]) => Number.isFinite(a) && Number.isFinite(b)).sort((x, y) => x[0] - y[0]);
  const out: Array<[number, number]> = [];
  for (const [a, b] of sorted) {
    const last = out[out.length - 1];
    if (last !== undefined && a <= last[1]) last[1] = Math.max(last[1], b);
    else out.push([a, b]);
  }
  return { ranges: out };
}

function widen(w: NoteWindow, by: number): NoteWindow {
  return mergeRanges(w.ranges.map(([a, b]) => [a - by, b + by] as const));
}

export function createSteppedCheckRunner(
  base: Omit<CheckContext, 'emit'>,
  opts: CheckRunnerOptions = {},
): SteppedCheckRunner {
  let rules: CheckRule[] = [];
  let enabled: ReadonlySet<string> = new Set();
  let thresholds: CheckThresholds = base.thresholds;
  let env: CheckEnvironment = {
    player: base.player,
    skin: base.skin,
    refViewport: base.refViewport,
    songLengthSec: base.songLengthSec,
    songLengthAsset: base.songLengthAsset ?? null,
  };
  let level: CheckLevel = 'full';
  /** The result: every problem shown, by id. */
  let current = new Map<string, Entry>();
  let currentList: readonly Problem[] | null = [];
  /** What each unit reported in the pass that last ran it. */
  let units = new Map<UnitKey, ReadonlyMap<string, Problem>>();
  /** Units of each rule that hold problems. */
  let ruleUnits = new Map<string, Set<UnitKey>>();
  /** Rules whose result covers every unit (a pass ran all of them since they were last dropped). */
  let complete = new Set<string>();
  /** The next pass runs everything (rules, thresholds, environment or level changed; after a reset). */
  let forceFull = true;
  /** The document's "Ignore here" list the last pass started with (identity first: lists are immutable). */
  let lastIgnored: readonly string[] = base.doc.editor.ignoredProblems;
  /**
   * Rules with a problem id that was ignored or brought back since they last ran completely: rules leave ignored
   * problems out (geometry.ts `emitProblem`), so they run everywhere again.
   */
  const ignoreDirty = new Set<string>();
  let sink: Sink | null = null;
  /** The note window of the windowed unit running now (read by geometry.ts `notesByTime`). */
  let noteWindow: NoteWindow | undefined;

  const report = (f: RuleFailure): void => {
    opts.onRuleError?.(f);
  };

  // The context rules see: everything of `base` (document, indexes, tempo, engine and any runtime extension such as
  // shader sources) through the prototype, with the runner's settings, `emit` and the note window on top.
  const ctx = Object.create(base) as CheckContext;
  Object.defineProperties(ctx, {
    thresholds: { get: () => thresholds, enumerable: true },
    player: { get: () => env.player, enumerable: true },
    skin: { get: () => env.skin, enumerable: true },
    refViewport: { get: () => env.refViewport, enumerable: true },
    songLengthSec: { get: () => env.songLengthSec, enumerable: true },
    songLengthAsset: { get: () => env.songLengthAsset, enumerable: true },
    noteWindow: { get: () => noteWindow, enumerable: false },
    emit: {
      value: (d: ProblemDraft): void => {
        sink?.emit(d);
      },
      enumerable: true,
    },
  });

  /**
   * In Guided, a 'full' rule with a feature probe runs only when the probe says the chart uses the feature; a probe
   * that throws is reported and the rule runs.
   */
  const shownAtLevel = (rule: CheckRule): boolean => {
    if (level !== 'guided' || rule.level !== 'full') return true;
    if (rule.usesFeature === undefined) return true;
    try {
      return rule.usesFeature(ctx);
    } catch (error) {
      report({ rule: rule.id, error });
      return true;
    }
  };

  const runnable = (): CheckRule[] => rules.filter((r) => enabled.has(r.id) && shownAtLevel(r));

  // ---- merging results into the current one ----

  const own = (rule: string, u: UnitKey, holds: boolean): void => {
    let set = ruleUnits.get(rule);
    if (holds) {
      if (set === undefined) {
        set = new Set();
        ruleUnits.set(rule, set);
      }
      set.add(u);
    } else if (set !== undefined) {
      set.delete(u);
      if (set.size === 0) ruleUnits.delete(rule);
    }
  };

  /** Replaces what a unit reported with `next`. */
  const mergeUnit = (rule: string, u: UnitKey, next: ReadonlyMap<string, Problem>, diff: DiffBuilder): void => {
    const prev = units.get(u);
    if (prev !== undefined) {
      for (const id of prev.keys()) {
        if (next.has(id)) continue;
        const e = current.get(id);
        if (e?.unit !== u) continue;
        current.delete(id);
        diff.remove(id);
      }
    }
    for (const [id, problem] of next) {
      const e = current.get(id);
      if (e === undefined) {
        current.set(id, { problem, sig: null, unit: u });
        diff.add(problem);
        continue;
      }
      // Another unit reported this id first (ids are stable entity keys: it is the same problem).
      if (e.unit !== u || e.problem === problem) continue;
      const entry: Entry = { problem, sig: null, unit: u };
      if (sigOf(e) !== sigOf(entry)) diff.update(problem);
      current.set(id, entry);
    }
    if (next.size === 0) units.delete(u);
    else units.set(u, next);
    own(rule, u, next.size > 0);
  };

  /** A windowed unit: the problems anchored inside the window are replaced by `found`, the others kept. */
  const windowed = (u: UnitKey, anchors: NoteWindow, found: ReadonlyMap<string, Problem>): Map<string, Problem> => {
    const next = new Map<string, Problem>();
    for (const [id, p] of units.get(u) ?? NO_PROBLEMS) {
      const sec = p.at.sec;
      if (sec !== undefined && inWindow(anchors, sec)) continue;
      next.set(id, p);
    }
    for (const [id, p] of found) if (!next.has(id)) next.set(id, p);
    return next;
  };

  const dropRule = (rule: string, diff: DiffBuilder): void => {
    for (const u of [...(ruleUnits.get(rule) ?? [])]) mergeUnit(rule, u, NO_PROBLEMS, diff);
    ruleUnits.delete(rule);
    complete.delete(rule);
  };

  const mergeFinished = (f: Finished, diff: DiffBuilder): void => {
    if (f.kind === 'unit') {
      mergeUnit(f.rule, f.unit, f.anchors === undefined ? f.problems : windowed(f.unit, f.anchors, f.problems), diff);
      return;
    }
    // A complete job: units it did not run (lines that are gone) are dropped.
    for (const u of [...(ruleUnits.get(f.rule) ?? [])]) if (!f.units.has(u)) mergeUnit(f.rule, u, NO_PROBLEMS, diff);
    complete.add(f.rule);
    ignoreDirty.delete(f.rule);
  };

  /** Notes the rules of every id added to or removed from the "Ignore here" list since the last pass. */
  const noteIgnoreChanges = (): void => {
    const now = base.doc.editor.ignoredProblems;
    if (now === lastIgnored) return;
    const before = new Set(lastIgnored);
    const after = new Set(now);
    for (const id of before) if (!after.has(id)) ignoreDirty.add(ruleOfId(id));
    for (const id of after) if (!before.has(id)) ignoreDirty.add(ruleOfId(id));
    lastIgnored = now;
  };

  // ---- planning ----

  /** Anchor ranges around the notes a scope changed (null: unknown, check everything). */
  const changedNotes = (scope: PassScope): NoteWindow | null => {
    if (scope.noteSpansUnknown || scope.noteSpans.length === 0) return null;
    const tempo = base.tempo;
    return mergeRanges(scope.noteSpans.map(([t, end]) => [tempo.secAt(t), tempo.secAt(end)] as const));
  };

  const plan = (scope: PassScope, everything: boolean, list: readonly CheckRule[]): Job[] => {
    const doc = base.doc;
    const jobs: Job[] = [];
    let dirty: Set<LineId> | null = null;
    let changed: NoteWindow | null | undefined;
    for (const rule of [...list].sort(byPhaseThenId)) {
      const all: Job = { rule, lines: rule.scope === 'line' ? doc.lineOrder : null, complete: true };
      if (everything || !complete.has(rule.id) || ignoreDirty.has(rule.id)) {
        jobs.push(all);
        continue;
      }
      if (rule.scope === 'chart') {
        const touched = scope.touches('lines') || rule.deps.some((d) => scope.touches(d));
        if (!touched) continue;
        // Only notes changed: a windowed rule checks around them.
        const onlyNotes = !scope.touches('lines') && rule.deps.every((d) => d === 'notes' || !scope.touches(d));
        if (onlyNotes && isWindowedRule(rule)) {
          changed ??= changedNotes(scope);
          if (changed !== null) {
            const pad = rule.windowPadSec(ctx) + WINDOW_SLACK_SEC;
            const anchors = widen(changed, pad);
            jobs.push({ rule, lines: null, complete: false, window: { anchors, notes: widen(anchors, pad) } });
            continue;
          }
        }
        jobs.push(all);
        continue;
      }
      if (GLOBAL_LINE_DEPS.some((d) => rule.deps.includes(d) && scope.touches(d))) {
        jobs.push(all);
        continue;
      }
      dirty ??= scope.dirtyLines(base.idx, (l) => doc.lines.has(l));
      if (dirty.size === 0) continue;
      const lines = doc.lineOrder.filter((l) => dirty?.has(l) === true);
      if (lines.length > 0) jobs.push({ rule, lines, complete: false });
    }
    return jobs;
  };

  // ---- passes ----

  const begin = (scopeArg?: PassScope): CheckPass => {
    const requested = scopeArg ?? PassScope.full();
    const everything = forceFull || requested.everything;
    forceFull = false;
    // A pass that runs everything makes every rule incomplete until its run is taken: a rule this pass does not get
    // to (it is abandoned) runs everywhere in the next pass whatever its scope, and the rules it finished stay done.
    // So an abandoned full pass hands nothing back, and a long first pass still makes progress while the user edits.
    if (everything) complete.clear();
    const scope = everything ? new PassScope() : requested;
    noteIgnoreChanges();
    const list = runnable();
    const shown = new Set(list.map((r) => r.id));
    const drops = [...new Set([...ruleUnits.keys(), ...complete])].filter((id) => !shown.has(id));
    const jobs = plan(scope, everything, list);
    const blocking = jobs.filter((j) => j.rule.category === BLOCKING_CATEGORY);
    const blockingJobs = blocking.length;
    const blockingUnits = blocking.reduce((n, j) => n + (j.lines === null ? 1 : j.lines.length), 0);
    const unitsTotal = jobs.reduce((n, j) => n + (j.lines === null ? 1 : j.lines.length), 0);

    /** Rules that failed in this pass: what they emitted is dropped, their previous problems are kept. */
    const failed = new Set<string>();
    const finished: Finished[] = [];
    /** Units of the running job (for its 'job' marker). */
    let jobUnits = new Set<UnitKey>();
    let jobIndex = 0;
    let lineIndex = 0;
    let unitsDone = 0;
    let dropsPending = drops.length > 0;
    let committed = false;

    const finishJob = (): void => {
      const job = jobs[jobIndex] as Job;
      if (!failed.has(job.rule.id) && job.complete) finished.push({ kind: 'job', rule: job.rule.id, units: jobUnits });
      jobUnits = new Set();
      jobIndex++;
      lineIndex = 0;
    };

    /** Finishes jobs without units (a line rule on a chart without lines). */
    const skipEmpty = (): void => {
      for (let job = jobs[jobIndex]; job?.lines?.length === 0; job = jobs[jobIndex]) finishJob();
    };

    const runUnit = (job: Job, line: LineId | undefined): void => {
      const rule = job.rule;
      if (failed.has(rule.id)) return;
      const drafts: ProblemDraft[] = [];
      sink = {
        emit: (d) => {
          drafts.push(d);
        },
      };
      noteWindow = job.window?.notes;
      try {
        if (line === undefined) rule.check(ctx);
        else rule.check(ctx, line);
      } catch (error) {
        failed.add(rule.id);
        // What the rule reported earlier in this pass and was not handed out yet is dropped with it.
        for (let i = finished.length - 1; i >= 0; i--)
          if ((finished[i] as Finished).rule === rule.id) finished.splice(i, 1);
        report(line === undefined ? { rule: rule.id, error } : { rule: rule.id, line, error });
        return;
      } finally {
        sink = null;
        noteWindow = undefined;
      }
      const anchors = job.window?.anchors;
      const found = new Map<string, Problem>();
      for (const d of drafts) {
        const bad = draftError(d);
        if (bad !== null) {
          report(line === undefined ? { rule: rule.id, error: bad } : { rule: rule.id, line, error: bad });
          continue;
        }
        // A windowed unit keeps what is anchored in its window (the rest saw only part of its notes).
        if (anchors !== undefined && (d.at.sec === undefined || !inWindow(anchors, d.at.sec))) continue;
        // The first draft of an id wins (ids are stable entity keys; a duplicate is the same problem).
        if (!found.has(d.id)) found.set(d.id, problemFromDraft(rule, d));
      }
      const u = unitKey(rule.id, line);
      jobUnits.add(u);
      finished.push(
        anchors === undefined
          ? { kind: 'unit', rule: rule.id, unit: u, problems: found }
          : { kind: 'unit', rule: rule.id, unit: u, problems: found, anchors },
      );
    };

    const takeDiff = (): ProblemDiff => {
      const diff = new DiffBuilder();
      if (dropsPending) {
        dropsPending = false;
        for (const id of drops) dropRule(id, diff);
      }
      for (const f of finished.splice(0)) mergeFinished(f, diff);
      const out = diff.take();
      if (!isEmptyDiff(out)) currentList = null;
      return out;
    };

    skipEmpty();
    const pass: CheckPass = {
      get done() {
        return jobIndex >= jobs.length;
      },
      get unitsDone() {
        return unitsDone;
      },
      unitsTotal,
      get blockingDone() {
        return jobIndex >= blockingJobs;
      },
      blockingUnits,
      scope,
      get failedRules() {
        return [...failed].sort();
      },
      run(shouldYield) {
        while (jobIndex < jobs.length) {
          const job = jobs[jobIndex] as Job;
          const line = job.lines === null ? undefined : job.lines[lineIndex];
          runUnit(job, line);
          unitsDone++;
          lineIndex++;
          if (job.lines === null || lineIndex >= job.lines.length) finishJob();
          skipEmpty();
          if (jobIndex < jobs.length && shouldYield?.() === true) break;
        }
        return jobIndex >= jobs.length;
      },
      takeDiff,
      commit() {
        if (jobIndex < jobs.length) throw new Error('CheckPass.commit: the pass has not finished');
        if (committed) throw new Error('CheckPass.commit: the pass was already committed');
        committed = true;
        return takeDiff();
      },
    };
    return pass;
  };

  return {
    setRules(next, on) {
      rules = [...next].sort(byId);
      enabled = new Set(on);
      forceFull = true;
    },
    setThresholds(t) {
      thresholds = t;
      forceFull = true;
    },
    setEnvironment(e) {
      env = {
        player: e.player,
        skin: e.skin,
        refViewport: e.refViewport,
        songLengthSec: e.songLengthSec,
        songLengthAsset: e.songLengthAsset ?? null,
      };
      forceFull = true;
    },
    setLevel(l) {
      if (l !== level) forceFull = true;
      level = l;
    },
    full(isStale) {
      const pass = begin();
      while (!pass.done) {
        if (isStale?.() === true) return null;
        pass.run(() => true);
      }
      if (isStale?.() === true) return null;
      return pass.commit();
    },
    problems() {
      currentList ??= [...current.values()].map((e) => e.problem);
      return currentList;
    },
    begin,
    reset() {
      current = new Map();
      currentList = null;
      units = new Map();
      ruleUnits = new Map();
      complete = new Set();
      ignoreDirty.clear();
      lastIgnored = base.doc.editor.ignoredProblems;
      forceFull = true;
    },
    get rules() {
      return rules;
    },
    get enabled() {
      return enabled;
    },
    get thresholds() {
      return thresholds;
    },
    get environment() {
      return env;
    },
    get level() {
      return level;
    },
  };
}

export const createCheckRunner: CreateCheckRunner = (ctx) => createSteppedCheckRunner(ctx);
